import React, { Component } from 'react';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { ThemeProvider } from '@material-ui/styles';
import validate from './utils/validate';

import { chartjs } from './helpers';
import theme from './theme';
import 'react-perfect-scrollbar/dist/css/styles.css';
import './assets/scss/index.scss';
import validators from './common/validators';
import Routes from './Routes';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import PostHogUserIdentifier from './components/PostHogUserIdentifier';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import { HelmetProvider } from 'react-helmet-async';
import { SessionTimeoutWarning } from './components';

// Import session testing utility in development
if (process.env.NODE_ENV === 'development') {
  require('./utils/sessionTest');
}

// Conditionally import AppInsights to avoid issues on PDF routes
let reactPlugin = null;
let AppInsightsContext = null;
let AppInsightsErrorBoundary = null;

const currentPath = window.location.pathname;
const isPdfRoute = currentPath.includes('/pdf/') ||
    currentPath.includes('/html/') ||
    currentPath.includes('/disconnect') ||
    currentPath.includes('/PrivacyPolicy') ||
    currentPath.includes('/TermsOfService');

if (!isPdfRoute) {
    try {
        const appInsightsModule = require("./AppInsights");
        reactPlugin = appInsightsModule.reactPlugin;

        // Only import React components if reactPlugin exists
        if (reactPlugin) {
            const reactModule = require("@microsoft/applicationinsights-react-js");
            AppInsightsContext = reactModule.AppInsightsContext;
            AppInsightsErrorBoundary = reactModule.AppInsightsErrorBoundary;
        }
    } catch (error) {
        console.warn('Error loading AppInsights in App.js:', error);
        reactPlugin = null;
        AppInsightsContext = null;
        AppInsightsErrorBoundary = null;
    }
}


const browserHistory = createBrowserHistory({
    basename: process.env.PUBLIC_URL
});

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// For Chart.js v3+, we need to register the custom draw function differently
// This extends the bar element with rounded corners
if (ChartJS.elements && ChartJS.elements.BarElement) {
    ChartJS.elements.BarElement.prototype.draw = chartjs.draw;
}

validate.validators = {
    ...validate.validators,
    ...validators
};

export default class App extends Component {
    componentDidMount() {
        // Debug environment variables in development
        if (process.env.NODE_ENV === 'development') {
            console.log('=== ENVIRONMENT DEBUG ===');
            console.log('NODE_ENV:', process.env.NODE_ENV);
            console.log('REACT_APP_ENV:', process.env.REACT_APP_ENV);
            console.log('All REACT_APP_ variables:', 
                Object.keys(process.env)
                    .filter(key => key.startsWith('REACT_APP_'))
                    .reduce((obj, key) => {
                        obj[key] = process.env[key];
                        return obj;
                    }, {})
            );
            console.log('=========================');
        }
    }

    render() {
        const AppContent = () => (
            <ChunkErrorBoundary>
                <HelmetProvider>
                    <ThemeProvider theme={theme}>
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                            <DndProvider backend={HTML5Backend}>
                                <Router history={browserHistory}>
                                    <PostHogUserIdentifier />
                                     <SessionTimeoutWarning />
                                    <Routes />
                                </Router>
                            </DndProvider>
                        </MuiPickersUtilsProvider>
                    </ThemeProvider>
                </HelmetProvider>
            </ChunkErrorBoundary>
        );

        // Only wrap with AppInsights components if they're available (not on PDF routes)
        if (AppInsightsErrorBoundary && AppInsightsContext && reactPlugin) {
            return (
                <AppInsightsErrorBoundary onError={() => {
                }} appInsights={reactPlugin}>
                    <AppInsightsContext.Provider value={reactPlugin}>
                        <AppContent />
                    </AppInsightsContext.Provider>
                </AppInsightsErrorBoundary>
            );
        } else {
            // For PDF routes, render without AppInsights components
            return <AppContent />;
        }
    }
}
